import axios from 'axios';
import {
  apiGetCity,
  apiGetPincodeByCityId,
  apiGetState,
  apiPanBlacklist,
  apiPanCustomer,
  apiSaveEnquiry,
  apiSaveEnquiryKeepalive,
  apiSaveLead,
  apiSendAadhaarOtp,
  apiVerifyAadhaarOtp,
  apiVerifyPanGov,
} from './apiGateway';

const isLocal =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

/** All APIs use dhanwalle.in (proxy on localhost) */
export const TASK_API_BASE = isLocal
  ? '/Api/TaskApi'
  : 'https://dhanwalle.in/Api/TaskApi';

export const LOAN_BACKEND_BASE = isLocal
  ? '/loan_backend'
  : 'https://dhanwalle.in/loan_backend';

export const API_ORIGIN = isLocal ? '' : 'https://dhanwalle.in';

/**
 * Flatten KYC responses.
 * Flat shape (send/verify Aadhaar):
 * { referenceId, responseMessage, responseCode, data? }
 */
export function normalizeKycResponse(payload) {
  const root = payload || {};
  const nested = root.data && typeof root.data === 'object' ? root.data : null;

  // Wrapper: { success, data: { responseCode, responseMessage, referenceId, data? } }
  if (nested && (nested.responseCode || nested.responseMessage)) {
    return {
      responseCode: nested.responseCode || root.responseCode || '',
      responseMessage:
        nested.responseMessage ||
        nested.message ||
        root.responseMessage ||
        root.message ||
        '',
      referenceId: nested.referenceId || root.referenceId || '',
      data: nested.data !== undefined ? nested.data : null,
      success: root.success,
      status_code: root.status_code,
      raw: root,
    };
  }

  // Flat shape used by dhanwalle loan_backend Aadhaar APIs
  return {
    responseCode: root.responseCode || '',
    responseMessage: root.responseMessage || root.message || root.Message || '',
    referenceId: root.referenceId || '',
    data: root.data !== undefined ? root.data : null,
    success: root.success,
    status_code: root.status_code,
    raw: root,
  };
}

export async function verifyPan(pan) {
  const resp = await apiVerifyPanGov(pan);
  return normalizeKycResponse(resp.data);
}

export async function sendAadhaarOtp(aadhaarNumber) {
  const resp = await apiSendAadhaarOtp(aadhaarNumber);
  return normalizeKycResponse(resp.data);
}

/** Verify OTP — body: aadhaar_number + otp (referenceId optional) */
export async function verifyAadhaarOtp(aadhaarNumber, otp, referenceId) {
  const body = {
    aadhaar_number: aadhaarNumber,
    otp: String(otp),
  };
  if (referenceId) body.referenceId = referenceId;

  const resp = await apiVerifyAadhaarOtp(body);
  return normalizeKycResponse(resp.data);
}

export async function getStates() {
  const resp = await apiGetState();
  return resp.data;
}

export async function getCities(stateId) {
  const resp = await apiGetCity(stateId);
  return resp.data;
}

export async function getPincodesByCityId(cityId) {
  const resp = await apiGetPincodeByCityId(cityId);
  return resp.data;
}

export async function checkPanBlacklist(pan) {
  const resp = await apiPanBlacklist(pan);
  return resp.data || {};
}

export async function checkExistingCustomerByPan(pan) {
  const resp = await apiPanCustomer(pan);
  return resp.data || {};
}

/**
 * Multi-vertical PAN check across loan companies.
 * GET https://dhanwalle.in/checkGetCustomerbyPan/{PAN}
 */
export async function checkMultiVerticalPan(pan) {
  const url = `https://dhanwalle.in/checkGetCustomerbyPan/${encodeURIComponent(pan)}`;

  const resp = await axios.get(url, { validateStatus: () => true });
  return resp.data || {};
}

/** Max DISBURSED verticals allowed before blocking new apply */
export const MAX_DISBURSED_VERTICALS = 2;

/**
 * Count verticals that have at least one DISBURSED record.
 * Returns { allowed, disbursedCount, disbursedVerticals, message }
 *
 * status:false / no data = fresh PAN → allow (other APIs decide eligibility)
 * Only blocks when DISBURSED in 2+ verticals.
 */
export function evaluateMultiVerticalPan(apiData) {
  const verticals = Array.isArray(apiData?.data) ? apiData.data : [];

  // Fresh / not found / empty → do not block here
  if (apiData?.status === false || !verticals.length) {
    return {
      allowed: true,
      disbursedCount: 0,
      disbursedVerticals: [],
      message: '',
    };
  }

  const disbursedVerticals = verticals
    .filter((v) =>
      (v.records || []).some(
        (r) => String(r.lead_status || '').toUpperCase() === 'DISBURSED'
      )
    )
    .map((v) => v.database);

  const disbursedCount = disbursedVerticals.length;

  // Already DISBURSED in 2+ companies → block applying again
  if (disbursedCount >= MAX_DISBURSED_VERTICALS) {
    return {
      allowed: false,
      disbursedCount,
      disbursedVerticals,
      message:
        'You have already taken a loan from another NBFC. A new application cannot be submitted at this time.',
    };
  }

  return {
    allowed: true,
    disbursedCount,
    disbursedVerticals,
    message: '',
  };
}

export async function saveLendingPage(payload) {
  // Live: gateway l1 (OTP session inject server-side). Local: save-application proxy.
  const resp = await apiSaveLead(payload);
  return resp.data || {};
}

/** LMS Enquires autosave — not used on final lead Submit. */
export const SAVE_ENQUIRY_URL = 'https://dhanwalle.in/loan_backend/saveEnquiry.php';

export function buildEnquiryPayload({
  formData = {},
  otpVerified = false,
  cityId = '',
  step = 'form',
  aadhaarNumber = '',
} = {}) {
  const mobile = String(formData.mobile || '')
    .replace(/\D/g, '')
    .slice(-10);
  if (mobile.length !== 10) return null;

  const first = String(formData.firstName || '').trim();
  const middle = String(formData.middleName || '').trim();
  const last = String(formData.lastName || '').trim();
  const fullName = [first, middle, last].filter(Boolean).join(' ');
  const pan = String(formData.panNumber || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 10);
  const aadhaar = String(
    aadhaarNumber || formData.aadhaarNumber || ''
  ).replace(/\D/g, '');
  const email = String(formData.email || '').trim();
  const cityName = String(
    formData.city || formData.city_name || ''
  ).trim();

  return {
    Mobile: mobile,
    mobile,
    Name: fullName || first,
    name: fullName || first,
    first_name: first,
    mobile_verified: otpVerified ? 1 : 0,
    pan: pan || '',
    pancard: pan || '',
    aadhaar: aadhaar || '',
    aadhaar_number: aadhaar || '',
    email: email || '',
    email_personal: email || '',
    loan_amount: formData.loanAmount || '',
    city_id: cityId || '',
    city_name: cityName,
    step: step || 'form',
    source: 'Dhanwalle',
  };
}

export async function saveEnquiry(payload) {
  if (!payload?.mobile && !payload?.Mobile) return null;
  // TEMP: saveEnquiry API disabled — re-enable when needed
  return null;
  /*
  try {
    const resp = await apiSaveEnquiry(payload);
    return resp.data || null;
  } catch (err) {
    console.warn('[saveEnquiry]', err?.message || err);
    return null;
  }
  */
}

/** pagehide / beforeunload — never blocks UX */
export function saveEnquiryKeepalive(payload) {
  if (!payload?.mobile && !payload?.Mobile) return;
  // TEMP: saveEnquiry keepalive disabled — re-enable when needed
  /*
  apiSaveEnquiryKeepalive(payload);
  */
}

/**
 * Debounced enquiry autosave controller.
 * Call setLeadSubmitting(true) before final Submit so saveEnquiry is skipped.
 */
export function createEnquiryAutosave({ getContext, debounceMs = 600 } = {}) {
  let timer = null;
  let leadSubmitting = false;

  const build = (step) => {
    if (typeof getContext !== 'function') return null;
    const ctx = getContext() || {};
    return buildEnquiryPayload({ ...ctx, step: step || ctx.step || 'form' });
  };

  const flush = (step, { keepalive = false } = {}) => {
    if (leadSubmitting) return;
    const payload = build(step);
    if (!payload) return;
    // TEMP: saveEnquiry autosave disabled — re-enable when needed
    /*
    if (keepalive) saveEnquiryKeepalive(payload);
    else saveEnquiry(payload);
    */
  };

  const schedule = (step) => {
    if (leadSubmitting) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      flush(step);
    }, debounceMs);
  };

  return {
    schedule,
    flushNow: flush,
    setLeadSubmitting: (v) => {
      leadSubmitting = !!v;
      if (leadSubmitting && timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
    cancel: () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export function isLeadSaveSuccess(data) {
  return data?.Status === 1 || data?.status === 1 || data?.success === true;
}

export function isPanVerifySuccess(normalized) {
  const code = String(normalized?.responseCode || '').toUpperCase();
  const status = String(
    normalized?.data?.status || normalized?.data?.Status || ''
  ).toUpperCase();
  return code === 'SRC001' && (!status || status === 'VALID');
}

export const PAN_NOT_FOUND_MESSAGE =
  'PAN does not exist. Enter your correct PAN.';

/** verifyPan.php — PAN not found (e.g. ENI004 / No Information Found.) */
export function isPanNotFoundResponse(normalized) {
  const code = String(normalized?.responseCode || '').toUpperCase();
  const msg = String(normalized?.responseMessage || '').toLowerCase();
  return (
    code === 'ENI004' ||
    msg.includes('no information found') ||
    msg.includes('pan not found in any database') ||
    (msg.includes('not found') && code !== 'SRC001')
  );
}

export function getPanVerifyErrorMessage(normalized) {
  if (isPanNotFoundResponse(normalized)) {
    return PAN_NOT_FOUND_MESSAGE;
  }
  return (
    normalized?.responseMessage ||
    normalized?.message ||
    'Government PAN verification failed'
  );
}

export function isAadhaarSendSuccess(normalized) {
  return String(normalized?.responseCode || '').toUpperCase() === 'SOS174';
}

export function isAadhaarVerifySuccess(normalized) {
  return String(normalized?.responseCode || '').toUpperCase() === 'SRC001';
}

const PAN_CACHE_KEY = 'fundswall_verify_pan_cache';

export function getPanCache(pan) {
  try {
    const raw = localStorage.getItem(PAN_CACHE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    return map?.[pan] || null;
  } catch {
    return null;
  }
}

export function setPanCache(pan, payload) {
  try {
    const raw = localStorage.getItem(PAN_CACHE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[pan] = payload;
    localStorage.setItem(PAN_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function clearPanCache() {
  try {
    localStorage.removeItem(PAN_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Build address string from Aadhaar verify data fields */
export function buildAadhaarAddress(d = {}) {
  return [
    d.careOf,
    d.house,
    d.street,
    d.locality,
    d.vtcName,
    d.subDistrict,
    d.district,
    d.state,
    d.pincode,
    d.country,
  ]
    .map((x) => String(x || '').trim())
    .filter(Boolean)
    .join(', ');
}
